import type { NormalizedDspResult } from "../../types";

import {
  AnalysisResult,
  ChordValidation,
  PerformanceIntegrityPack,
  MatchType,
  ToneDetails,
  RawDspMetrics,
  IntegrityStatus,
  CalibrationStatus,
  PeakDetail,
  GhostNoteData
} from "../../types";

import { ScoreReport } from "../scoreTypes";
import { APP_VERSION } from "../config";
import { registerURLManual } from "../memoryService";
import { ANALYSIS_INVARIANTS } from "../../analysis/analysisInvariants";
import { sealResult } from "../../policy/integrityProtocol";
import { getRuntimeContext } from "../runtimeContext";

export interface FactoryInput {
  sessionId: string;
  dsp: NormalizedDspResult;
  report: ScoreReport;
  harmonyRes: { gridValidation: ChordValidation[]; harmonyFromGrid: number | null };
  gridTokens: string[];
  targetBpm: number;
  activeLevelId: number;
  stuHash: string;
  studentSource: string;
  stuFile: File;
  refFile: File | null;
  fallbackActive: boolean;
  qualityWarning?: string;
  matchType?: MatchType;
  integrityStatus: IntegrityStatus;
  calibrationStatus?: CalibrationStatus;
}

/**
 * NO=100% : si le DSP ne donne pas la donnée, on n’invente rien.
 * rawMetrics vide + isMeasurable=false => l’UI doit verrouiller l’onglet.
 */
const EMPTY_RAW_METRICS: RawDspMetrics = {
  timingDeviationMs: [],
  onsetTimesMs: [],
  bpmDetected: 0,
  transientCount: 0,
  rms_db: -240,
  non_silence_ratio: 0,
  isMeasurable: false,
  transient_ratio: 0,
  peakedness: 0,
  stability: 0,
  nuance: { measurable: false, relativeDynamics: 0, coherence: 0 },
  onset_count: 0,
  envCorr: 0,
  lagMs: 0,
  hardwareLagMs: 0,
  startOffsetMs: 0,
  chromaClarity: 0,
  clip_ratio: 0
};

export class AnalysisResultFactory {
  static async create(input: FactoryInput): Promise<AnalysisResult> {
    const runtime = getRuntimeContext();

    const {
      sessionId,
      dsp,
      report,
      harmonyRes,
      gridTokens,
      targetBpm,
      activeLevelId,
      stuHash,
      studentSource,
      stuFile,
      refFile,
      fallbackActive,
      qualityWarning,
      matchType,
      integrityStatus,
      calibrationStatus = "VALID"
    } = input;

    // -----------------------------
    // NO=100% guardrail (anti-fallback)
    // -----------------------------
    // Option A (actuel): on ne throw pas, mais on rend non-certifiable.
    // Option B (hard): throw new Error(...) — si tu veux bloquer toute session “fallback”.
    const integrityPass = integrityStatus === "PASS";
    const certifiedBase = integrityPass && calibrationStatus === "VALID";
    const certified = certifiedBase && !fallbackActive;

    // Si fallbackActive, on force un statut UX explicite (zéro ambiguïté)
    const uxMessage = certified
      ? "Analyse vérifiée"
      : fallbackActive
        ? "Analyse NON certifiable : fallback interdit (NO=100%)"
        : "Analyse non certifiée";

    // -----------------------------
    // DSP payload (NO invention)
    // -----------------------------
    const spectral = dsp?.spectral || {
      stuChroma: [],
      refChroma: [],
      stuBands: { low: 0, mid: 0, high: 0 },
      refBands: { low: 0, mid: 0, high: 0 },
      stuFingerprint: [],
      refFingerprint: []
    };

    const rawMetrics: RawDspMetrics = dsp?.rawMetrics ? dsp.rawMetrics : EMPTY_RAW_METRICS;
    const hardwareLag = typeof dsp?.hardwareLagMs === "number" ? dsp.hardwareLagMs : 0;
    const startOffset = typeof dsp?.startOffsetMs === "number" ? dsp.startOffsetMs : 0;
    const duration = typeof dsp?.duration === "number" ? dsp.duration : 0;

    const toneDetails: ToneDetails = {
      deltaLow: spectral.stuBands.low - spectral.refBands.low,
      deltaMid: spectral.stuBands.mid - spectral.refBands.mid,
      deltaHigh: spectral.stuBands.high - spectral.refBands.high,
      stu: [spectral.stuBands.low, spectral.stuBands.mid, spectral.stuBands.high],
      ref: [spectral.refBands.low, spectral.refBands.mid, spectral.refBands.high]
    };

    const techPack: PerformanceIntegrityPack = {
      origin: studentSource,
      analysis_hash: "PENDING_SEAL",
      capture_hash: stuHash,
      reasons: report.reasons?.map((r: any) => r.label) || [],
      confidence: dsp?.confidence || 0,
      audio_source: studentSource,
      engine_mode: "LOCAL_DSP_STRICT",
      filenames: { ref: refFile?.name || "Master", stu: stuFile.name }
    };

    const stuUrl = registerURLManual(URL.createObjectURL(stuFile), sessionId);
    const refUrl = refFile ? registerURLManual(URL.createObjectURL(refFile), sessionId) : "";

    // NO=100% : si pas certifié => pas de score affiché
    const scoreDisplayed = certified ? report.score : null;

    // -----------------------------
    // NO=100% peaks (DSP only)
    // -----------------------------
    const stuPeaks: PeakDetail[] =
      Array.isArray(dsp?.peaks?.stu) && dsp.peaks.stu.length > 0 ? dsp.peaks.stu : [];

    const refPeaks: PeakDetail[] =
      Array.isArray(dsp?.peaks?.ref) && dsp.peaks.ref.length > 0 ? dsp.peaks.ref : [];

    // -----------------------------
    // NO=100% timbre fingerprint (DSP only)
    // -----------------------------
    const mfccStu: number[] = Array.isArray(spectral?.stuFingerprint) ? spectral.stuFingerprint : [];
    const mfccRef: number[] = Array.isArray(spectral?.refFingerprint) ? spectral.refFingerprint : [];
    const ghostNotes: GhostNoteData[] = Array.isArray(dsp?.ghostNotes) ? dsp.ghostNotes : [];

    const result: AnalysisResult = {
      app_version: APP_VERSION,
      coaching_level: activeLevelId,
      match_type: matchType,

      session: {
        score: scoreDisplayed,
        match_score: scoreDisplayed,
        quality_score: Math.round((dsp?.confidence || 0) * 100),
        rank: this.deriveRank(report.score),
        bpm: targetBpm,
        duration,
        lagMs: hardwareLag,
        playbackRatio: 1,

        // NO=100% : firstOnset ne doit pas “inventer” via defaults
        firstOnset: Array.isArray(rawMetrics.onsetTimesMs) && rawMetrics.onsetTimesMs.length > 0 ? rawMetrics.onsetTimesMs[0] : 0,

        metrics: {
          ...report.metrics,
          match_score: report.score || 0,
          debug: {
            ...rawMetrics,
            hardwareLagMs: hardwareLag,
            startOffsetMs: startOffset,
            lagMs: hardwareLag
          }
        },

        isUnmeasurable: report.status === "INVALID",

        truth_report: {
          status: report.status === "VALID" ? "OK" : "INVALID",
          integrity_status: integrityStatus,
          calibration_status: calibrationStatus,
          confidence: dsp?.confidence || 0,
          ux_message: uxMessage,
          score: report.score,
          match_score: report.score,
          score_reasons: report.reasons || [],
          tech_pack: techPack
        },

        audio_quality_warning: qualityWarning || null,
        certified
      },

      // Rhythm fields (NO=100%)
      rhythm: {
        refEnvelope: dsp?.envelopes?.ref || [],
        stuEnvelope: dsp?.envelopes?.stu || [],
        refPeaks,
        stuPeaks,
        // NO=100% : consistency = rawMetrics.stability mais si rawMetrics est vide, ça doit rester 0
        consistency: typeof rawMetrics?.stability === "number" ? rawMetrics.stability : 0
      },

      harmony: {
        progression: gridTokens,
        chromaRef: spectral.refChroma || [],
        chromaStu: spectral.stuChroma || [],
        gridValidation: harmonyRes.gridValidation || []
      },

      tone: {
        details: toneDetails,
        mfccData: mfccStu,
        mfccDataRef: mfccRef,
        ghostNotes
      },

      biomechanics: null,
      alignedUrls: { ref: refUrl, stu: stuUrl },

      feedback: {
        explanations: [],
        diagnostics: [],
        advice: [],
        exercises: []
      },

      audit_trail: {
        audio_hash: stuHash,
        engine_version: APP_VERSION,
        scoring_ruleset_id: `GSC_SESSION_${sessionId}`,
        metrics_used: ["full_neural_trace_v16"],

        // NO=100% : on ne cache pas. Si fallbackActive true => visible.
        fallback_used: fallbackActive,

        analysis_hash: "",
        local_seal: "",
        computed_at: new Date(runtime.now()).toISOString(),
        invariants: ANALYSIS_INVARIANTS,
        remote_ai_status: "UNAVAILABLE"
      }
    };

    return await sealResult(result);
  }

  private static deriveRank(score: number | null): string {
    if (score === null) return "UNRANKED";
    if (score >= 90) return "STUDIO MASTER";
    if (score >= 75) return "SESSION PLAYER";
    if (score >= 50) return "PRACTICING";
    return "BEGINNER";
  }
}
